import { useState, useMemo } from 'react'
import { useApp } from '@/store/AppContext'
import { useAuth } from '@/hooks/use-auth'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import { formatCurrency } from '@/lib/pricing'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { ChartContainer, ChartTooltipContent } from '@/components/ui/chart'
import {
  Banknote,
  CreditCard,
  Download,
  Calendar as CalendarIcon,
  ShieldAlert,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { isToday, differenceInDays, format, subDays, isSameDay } from 'date-fns'

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))',
  'hsl(var(--chart-3))',
]
const TYPE_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
]

export default function Reports() {
  const { payments, tickets } = useApp()
  const { profile } = useAuth()
  const [filterPeriod, setFilterPeriod] = useState('7days')

  const { filteredPayments, previousPayments } = useMemo(() => {
    const current = payments.filter((p) => {
      const d = new Date(p.paidAt)
      if (filterPeriod === 'today') return isToday(d)
      if (filterPeriod === '7days') return differenceInDays(new Date(), d) <= 7
      if (filterPeriod === '30days')
        return differenceInDays(new Date(), d) <= 30
      return true
    })

    const previous = payments.filter((p) => {
      const d = new Date(p.paidAt)
      if (filterPeriod === 'today') return isSameDay(d, subDays(new Date(), 1))
      if (filterPeriod === '7days')
        return (
          differenceInDays(new Date(), d) > 7 &&
          differenceInDays(new Date(), d) <= 14
        )
      if (filterPeriod === '30days')
        return (
          differenceInDays(new Date(), d) > 30 &&
          differenceInDays(new Date(), d) <= 60
        )
      return false
    })

    return { filteredPayments: current, previousPayments: previous }
  }, [payments, filterPeriod])

  const typeData = useMemo(() => {
    const data: Record<string, number> = { Carro: 0, Moto: 0, Outros: 0 }
    filteredPayments.forEach((p) => {
      const t = tickets.find((ticket) => ticket.id === p.ticketId)
      if (t) data[t.type] += p.amount
    })
    return Object.keys(data)
      .map((k) => ({ name: k, value: data[k] }))
      .filter((d) => d.value > 0)
  }, [filteredPayments, tickets])

  if (
    profile?.role !== 'admin' &&
    profile?.role !== 'ultramaster' &&
    profile?.role !== 'financial'
  ) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center space-y-4 animate-fade-in-up">
        <ShieldAlert className="h-16 w-16 text-destructive" />
        <h2 className="text-2xl font-bold">Acesso Negado</h2>
        <p className="text-muted-foreground">
          Apenas administradores e financeiros podem acessar relatórios
          avançados.
        </p>
      </div>
    )
  }

  const revenueCurrent = filteredPayments.reduce((acc, p) => acc + p.amount, 0)
  const revenuePrev = previousPayments.reduce((acc, p) => acc + p.amount, 0)
  const growth =
    revenuePrev > 0
      ? ((revenueCurrent - revenuePrev) / revenuePrev) * 100
      : revenueCurrent > 0
        ? 100
        : 0

  const avgTicket =
    filteredPayments.length > 0 ? revenueCurrent / filteredPayments.length : 0
  const avgPrev =
    previousPayments.length > 0 ? revenuePrev / previousPayments.length : 0
  const avgGrowth = avgPrev > 0 ? ((avgTicket - avgPrev) / avgPrev) * 100 : 0

  // Mock flow data for last 7 days
  const cashFlowData = Array.from({ length: 7 }, (_, i) => {
    const d = subDays(new Date(), 6 - i)
    return {
      date: format(d, 'dd/MM'),
      Receita: Math.floor(Math.random() * 500) + 100,
    }
  })

  const handleExportCSV = () => {
    const data = filteredPayments.map((p) => {
      const t = tickets.find((ticket) => ticket.id === p.ticketId)
      return {
        ID: p.id,
        Placa: t?.plate || '-',
        Tipo: t?.type || '-',
        Valor: p.amount.toFixed(2),
        Metodo: p.method,
        Data: format(new Date(p.paidAt), 'dd/MM/yyyy HH:mm'),
      }
    })

    if (data.length === 0) return
    const headers = Object.keys(data[0]).join(',')
    const rows = data.map((obj) => Object.values(obj).join(','))
    const csv = [headers, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `relatorio-parkko-${format(new Date(), 'yyyy-MM-dd')}.csv`
    link.click()
  }

  const renderGrowth = (value: number) => {
    if (value === 0)
      return (
        <span className="flex items-center text-xs text-muted-foreground mt-1">
          <Minus className="h-3 w-3 mr-1" /> Sem alteração
        </span>
      )
    if (value > 0)
      return (
        <span className="flex items-center text-xs text-emerald-600 font-medium mt-1">
          <ArrowUpRight className="h-3 w-3 mr-1" /> +{value.toFixed(1)}% vs
          anterior
        </span>
      )
    return (
      <span className="flex items-center text-xs text-destructive font-medium mt-1">
        <ArrowDownRight className="h-3 w-3 mr-1" /> {value.toFixed(1)}% vs
        anterior
      </span>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Relatórios Financeiros
          </h1>
          <p className="text-muted-foreground">
            Visão geral do desempenho financeiro e ocupação do pátio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterPeriod} onValueChange={setFilterPeriod}>
            <SelectTrigger className="w-[180px] bg-background font-medium h-10">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoje</SelectItem>
              <SelectItem value="7days">Últimos 7 Dias</SelectItem>
              <SelectItem value="30days">Últimos 30 Dias</SelectItem>
              <SelectItem value="all">Todo o período</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-sm border-l-4 border-l-primary">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Receita Total
            </CardTitle>
            <Banknote className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">
              {formatCurrency(revenueCurrent)}
            </div>
            {filterPeriod !== 'all' && renderGrowth(growth)}
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Volume de Veículos
            </CardTitle>
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{filteredPayments.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              tickets finalizados no período
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ticket Médio
            </CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">
              {formatCurrency(avgTicket)}
            </div>
            {filterPeriod !== 'all' && renderGrowth(avgGrowth)}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Receita por Tipo de Veículo</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px] flex items-center justify-center">
            {typeData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={typeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {typeData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={TYPE_COLORS[index % TYPE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val: number) => formatCurrency(val)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground">Sem dados.</p>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Fluxo de Receita (Últimos 7 dias)</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            <ChartContainer
              config={{
                Receita: { label: 'Receita R$', color: 'hsl(var(--primary))' },
              }}
              className="h-full w-full"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cashFlowData}>
                  <XAxis
                    dataKey="date"
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => `R$${val}`}
                  />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="Receita"
                    fill="var(--color-Receita)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <CardTitle>Extrato de Transações e Tickets Auditados</CardTitle>
            <CardDescription>
              Exportação em CSV compatível com sistemas contábeis. Apenas
              leitura.
            </CardDescription>
          </div>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="w-full md:w-auto font-medium shadow-sm"
          >
            <Download className="h-4 w-4 mr-2" /> Exportar CSV
          </Button>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Data e Hora</TableHead>
                <TableHead>Placa / Tipo</TableHead>
                <TableHead>Método</TableHead>
                <TableHead className="text-right">Valor Pago</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center h-24 text-muted-foreground"
                  >
                    Nenhuma transação encontrada no período.
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.slice(0, 15).map((p) => {
                  const t = tickets.find((ticket) => ticket.id === p.ticketId)
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        {format(new Date(p.paidAt), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <div className="font-bold tracking-widest">
                          {t?.plate || '----'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t?.type}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${p.method === 'Pix' ? 'bg-teal-50 text-teal-700' : p.method === 'Dinheiro' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}
                        >
                          {p.method}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-black">
                        {formatCurrency(p.amount)}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
          {filteredPayments.length > 15 && (
            <div className="p-4 text-center text-sm text-muted-foreground bg-muted/20 border-t">
              Mostrando 15 de {filteredPayments.length} transações. Exporte o
              CSV para ver todas.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
